<script setup>
import axios from 'axios'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
    action: {
        type: String,
        default: '/shop',
    },
    autocompleteUrl: {
        type: String,
        default: '/api/search/autocomplete',
    },
    placeholder: {
        type: String,
        default: 'Type your search query...',
    },
    buttonId: {
        type: String,
        default: 'header-search-button',
    },
    defaultImage: {
        type: String,
        default: '/images/default.jpg',
    },
})

const query = ref('')
const products = ref([])
const total = ref(0)
const isLoading = ref(false)
const hasSearched = ref(false)
const isOpen = ref(false)
const rootElement = ref(null)
let activeRequest = 0
let debounceTimeout = null
const visitorStorageKey = 'eg_search_visitor_id'

function generateVisitorToken() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID()
    }

    return `search-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

function getVisitorToken() {
    try {
        let token = window.localStorage.getItem(visitorStorageKey)

        if (!token) {
            token = generateVisitorToken()
        }

        window.localStorage.setItem(visitorStorageKey, token)

        return token
    } catch (error) {
        return ''
    }
}

const showResults = computed(() => {
    return isOpen.value
        && query.value.trim().length >= 2
        && (isLoading.value || products.value.length > 0 || hasSearched.value)
})

const resultsButtonLabel = computed(() => {
    return `See ${total.value} Results`
})

function submitSearch() {
    const trimmedQuery = query.value.trim()
    getVisitorToken()

    if (trimmedQuery === '') {
        window.location.href = props.action
        return
    }

    closeResults()
    window.location.href = `${props.action}?s=${encodeURIComponent(trimmedQuery)}`
}

function selectProduct(product) {
    closeResults()
    window.location.href = `/products/${product.handle}`
}

function openResults() {
    isOpen.value = true
}

function closeResults() {
    isOpen.value = false
}

async function fetchResults(nextQuery) {
    const trimmedQuery = nextQuery.trim()
    const requestId = ++activeRequest

    if (trimmedQuery.length < 2) {
        products.value = []
        total.value = 0
        hasSearched.value = false
        isLoading.value = false
        closeResults()
        return
    }

    openResults()
    isLoading.value = true
    console.log('saving search query:', trimmedQuery)

    try {
        const { data } = await axios.get(props.autocompleteUrl, {
            params: { q: trimmedQuery },
            headers: {
                'X-Search-Visitor-Id': getVisitorToken(),
            },
        })

        if (requestId !== activeRequest) {
            return
        }

        products.value = Array.isArray(data.products) ? data.products : []
        total.value = Number.isFinite(data.total) ? data.total : products.value.length
        hasSearched.value = true
    } catch (error) {
        if (requestId !== activeRequest) {
            return
        }

        products.value = []
        total.value = 0
        hasSearched.value = true
    } finally {
        if (requestId === activeRequest) {
            isLoading.value = false
        }
    }
}

function handleInput() {
    products.value = []
    total.value = 0
    hasSearched.value = false

    clearTimeout(debounceTimeout)
    debounceTimeout = setTimeout(() => {
        fetchResults(query.value)
    }, 750)
}

function handleFocus() {
    if (query.value.trim().length >= 2) {
        openResults()
    }
}

function handleDocumentClick(event) {
    if (!rootElement.value) {
        return
    }

    if (!rootElement.value.contains(event.target)) {
        closeResults()
    }
}

function handleDocumentKeydown(event) {
    if (event.key === 'Escape') {
        closeResults()
    }
}

onMounted(() => {
    getVisitorToken()
    document.addEventListener('click', handleDocumentClick)
    document.addEventListener('keydown', handleDocumentKeydown)
})

onBeforeUnmount(() => {
    clearTimeout(debounceTimeout)
    document.removeEventListener('click', handleDocumentClick)
    document.removeEventListener('keydown', handleDocumentKeydown)
})
</script>

<template>
    <div ref="rootElement">
        <form :action="action" role="search" @submit.prevent="submitSearch">
            <h2 class="visually-hidden">Site search</h2>
            <div class="input-group">
                <button class="btn btn-icon" type="submit" :id="buttonId">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.996 21">
                        <path data-name="Icon awesome-search" d="m20.711 18.156-4.089-4.089a.98.98 0 0 0-.7-.287h-.668a8.526 8.526 0 1 0-1.476 1.476v.668a.98.98 0 0 0 .287.7l4.089 4.089a.98.98 0 0 0 1.39 0l1.161-1.161a.99.99 0 0 0 0-1.394ZM8.53 13.78a5.249 5.249 0 1 1 5.25-5.25 5.246 5.246 0 0 1-5.25 5.25" fill="#a7be94"/>
                    </svg>
                </button>
                <input
                    v-model="query"
                    type="search"
                    name="s"
                    class="form-control search-input"
                    :placeholder="placeholder"
                    aria-label="Search"
                    :aria-describedby="buttonId"
                    autocomplete="off"
                    @focus="handleFocus"
                    @input="handleInput"
                >
            </div>
        </form>

        <div v-if="showResults" class="search-results">
            <div>
                <div v-if="isLoading" class="search-results__loading">
                    <div class="loader">Searching products...</div>
                </div>

                <template v-else>
                    <div v-if="products.length" class="products-grid grid">
                        <div
                            v-for="product in products"
                            :key="product.handle"
                            class="grid__item product-card"
                        >
                            <a href="#" @click.prevent="selectProduct(product)">
                                <figure class="product-card__image-area">
                                    <img :src="product.listing_image || product.image || defaultImage" alt="">
                                </figure>
                                <div class="product-card__content">
                                    <h3 class="product-card__title" v-html="product.display_html || product.html"></h3>
                                    <h4 v-if="product.is_in_stock && product.from_price" class="product-card__subtitle">
                                        From ${{ Number(product.from_price).toFixed(2) }}
                                    </h4>
                                    <h4 v-else-if="!product.is_in_stock" class="product-card__subtitle">
                                        {{ product.out_of_stock_message || 'Out of Stock' }}
                                    </h4>
                                </div>
                            </a>
                        </div>
                    </div>

                    <div v-else class="search-results__empty">
                        We couldn't find anything.
                    </div>

                    <a
                        v-if="total > 0"
                        href="#"
                        class="btn btn-primary btn-block search-results__button"
                        @click.prevent="submitSearch"
                    >
                        {{ resultsButtonLabel }}
                    </a>
                </template>
            </div>
        </div>
    </div>
</template>
